/**
 * Prescription presets ("saved prescriptions") — R32.
 *
 * Clinic-scoped reusable prescription templates. The wizard's prescription
 * assistant lists them, applies one to the form on click, and lets the
 * podiatrist save the current prescription as a new preset.
 *
 * `configuration` holds a PrescriptionDeepState snapshot (insole design +
 * modification sections). Patient data is never stored on a preset.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

type PresetInsert = Database['public']['Tables']['prescription_presets']['Insert'];
type PresetConfiguration =
  Database['public']['Tables']['prescription_presets']['Row']['configuration'];

export const prescriptionPresetsKeys = {
  all: ['prescription_presets'] as const,
  byClinic: (clinicId: string | null) =>
    [...prescriptionPresetsKeys.all, 'clinic', clinicId] as const,
};

export interface PrescriptionPresetRow {
  id: string;
  clinic_id: string;
  created_by: string | null;
  name: string;
  configuration: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

const PRESET_COLUMNS =
  'id, clinic_id, created_by, name, configuration, created_at, updated_at';

/** Narrow a row from the typed query (configuration: Json) to our exposed
 *  shape (configuration: Record<string, unknown>). */
function toPresetRow(row: {
  id: string;
  clinic_id: string;
  created_by: string | null;
  name: string;
  configuration: unknown;
  created_at: string;
  updated_at: string;
}): PrescriptionPresetRow {
  return {
    id: row.id,
    clinic_id: row.clinic_id,
    created_by: row.created_by,
    name: row.name,
    configuration: (row.configuration ?? {}) as Record<string, unknown>,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

/** TanStack query options: list a clinic's presets, newest first. */
export function prescriptionPresetsQuery(
  client: SupabaseClient<Database>,
  clinicId: string | null,
) {
  return {
    queryKey: prescriptionPresetsKeys.byClinic(clinicId),
    enabled: !!clinicId,
    queryFn: async (): Promise<PrescriptionPresetRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await client
        .from('prescription_presets')
        .select(PRESET_COLUMNS)
        .eq('clinic_id', clinicId)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map(toPresetRow);
    },
  };
}

export async function createPrescriptionPreset(
  client: SupabaseClient<Database>,
  input: {
    clinic_id: string;
    created_by?: string | null;
    name: string;
    configuration: Record<string, unknown>;
  },
): Promise<PrescriptionPresetRow> {
  const payload: PresetInsert = {
    clinic_id: input.clinic_id,
    created_by: input.created_by ?? null,
    name: input.name,
    configuration: input.configuration as PresetConfiguration,
  };
  const { data, error } = await client
    .from('prescription_presets')
    .insert(payload)
    .select(PRESET_COLUMNS)
    .single();
  if (error) throw error;
  return toPresetRow(data);
}

export async function deletePrescriptionPreset(
  client: SupabaseClient<Database>,
  id: string,
): Promise<void> {
  const { error } = await client.from('prescription_presets').delete().eq('id', id);
  if (error) throw error;
}
